import React, { useEffect, useRef, useState } from 'react';
import API from '../../api/api';
import { MediaLibraryItem } from '../../api/media';
import { Input } from '../ui';
import Modal from './Modal';
import { useToast } from '../../hooks/useToast';
import MediaGrid from '../ideas/attachment-library/MediaGrid';
import { Density } from '../ideas/attachment-library/mediaCardUtils';

interface MediaLibraryPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  filterType?: 'image' | 'video' | 'audio';
  onSelect: (item: MediaLibraryItem) => void;
}

const MAX_UPLOAD_BYTES = 64 * 1024 * 1024;
const PAGE_SIZE = 20;

// Module-level page-1 cache (dependency-free "keepPreviousData") so reopening the
// picker with the same filter/search paints instantly while revalidating in the
// background, instead of flashing a "Loading..." spinner every open.
interface PickerCacheEntry {
  items: MediaLibraryItem[];
  total: number;
}
const MAX_PICKER_CACHE = 16;
const pickerPageCache = new Map<string, PickerCacheEntry>();
const pickerCacheKey = (filterType: string | undefined, search: string): string =>
  `${filterType ?? 'all'}::${search.trim().toLowerCase()}`;
const readPickerCache = (filterType: string | undefined, search: string): PickerCacheEntry | undefined =>
  pickerPageCache.get(pickerCacheKey(filterType, search));
const writePickerCache = (filterType: string | undefined, search: string, value: PickerCacheEntry): void => {
  const key = pickerCacheKey(filterType, search);
  pickerPageCache.delete(key);
  pickerPageCache.set(key, value);
  while (pickerPageCache.size > MAX_PICKER_CACHE) {
    const oldest = pickerPageCache.keys().next().value;
    if (oldest === undefined) break;
    pickerPageCache.delete(oldest);
  }
};
export const __resetMediaPickerCacheForTests = (): void => {
  pickerPageCache.clear();
};

const MediaLibraryPickerModal: React.FC<MediaLibraryPickerModalProps> = ({
  isOpen,
  onClose,
  filterType,
  onSelect,
}) => {
  const toast = useToast();
  const [items, setItems] = useState<MediaLibraryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
      setItems([]);
    }, 300);
  };

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const apiType = filterType === 'video' ? 'video' : filterType;

    // On the first page, paint cached results instantly and revalidate in the
    // background (no spinner). Deeper pages always show their loading row.
    const cached = page === 1 ? readPickerCache(filterType, debouncedSearch) : undefined;
    if (cached) {
      setItems(cached.items);
      setTotal(cached.total);
    } else {
      setIsLoading(true);
    }

    API.listMediaLibraryPaged({
      type: apiType,
      search: debouncedSearch || undefined,
      page,
      limit: PAGE_SIZE,
    })
      .then((result) => {
        if (cancelled) return;
        setItems((prev) => page === 1 ? result.items : [...prev, ...result.items]);
        setTotal(result.total);
        if (page === 1) {
          writePickerCache(filterType, debouncedSearch, { items: result.items, total: result.total });
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Keep the cached paint on error; only blank a true cache miss.
          if (page === 1 && !cached) setItems([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, filterType, debouncedSearch, page]);

  // Reset on close/reopen
  useEffect(() => {
    if (!isOpen) {
      setItems([]);
      setTotal(0);
      setPage(1);
      setSearch('');
      setDebouncedSearch('');
    }
  }, [isOpen]);

  const handleSelect = (item: MediaLibraryItem) => {
    if (item.status === 'generating' || item.status === 'failed') return;
    onSelect(item);
    onClose();
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      toast({ text: 'File must be under 64 MB', level: 'error' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setIsUploading(true);
    try {
      const uploaded = await API.uploadMediaLibraryFile(file);
      setItems((prev) => [uploaded, ...prev]);
      setTotal((t) => t + 1);
      toast({ text: 'Uploaded successfully', level: 'success' });
    } catch (err: any) {
      toast({ text: `Upload failed: ${err?.message || 'Unknown error'}`, level: 'error' });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const hasMore = items.length < total && !isLoading;

  // The picker is a single-select grid: clicking a card selects + closes. It
  // reuses the same MediaGrid / MediaCard the manage library uses, so the look,
  // accessibility, and thumbnail pipeline stay consistent across every surface.
  const handleCardActivate = (mediaId: string) => {
    const item = items.find((it) => (it.media_id || it.id) === mediaId);
    if (item) handleSelect(item);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Pick ${filterType ?? 'Media'}`} size="xl">
      <div className="space-y-3">
        {/* Search + Upload */}
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              placeholder="Search prompts, names…"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <button
            onClick={handleUploadClick}
            disabled={isUploading}
            title="Max 64 MB"
            className="shrink-0 px-3 py-1.5 text-xs font-mono border border-[var(--border-subtle)] text-[var(--app-text-muted)] hover:text-[var(--app-text)] hover:border-[var(--border-strong)] transition-colors disabled:opacity-50"
          >
            {isUploading ? 'Uploading...' : '+ Upload'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {/* Loading spinner preserved for the cold/page-2 cache-miss path. */}
        {isLoading && items.length === 0 && (
          <p className="text-xs text-[var(--app-text-muted)] py-4 text-center font-mono">Loading...</p>
        )}

        {/* Contact-sheet grid */}
        <div className="max-h-[60vh] overflow-y-auto">
          {!(isLoading && items.length === 0) && (
            <MediaGrid
              items={items}
              selectedIds={[]}
              selectMode={false}
              density={'cozy' as Density}
              loading={false}
              togglingFavouriteId={null}
              hasActiveFilters={debouncedSearch.trim().length > 0}
              onActivate={handleCardActivate}
              onToggleSelect={handleCardActivate}
              onToggleFavourite={() => undefined}
              showFavourite={false}
            />
          )}

          {/* Load more */}
          {hasMore && (
            <button
              onClick={() => setPage((p) => p + 1)}
              className="w-full py-2 text-xs text-[var(--app-text-muted)] hover:text-[var(--app-text)] font-mono border border-[var(--border-subtle)] hover:border-[var(--border-strong)] transition-colors mt-2"
            >
              Load more ({total - items.length} remaining)
            </button>
          )}
          {isLoading && page > 1 && (
            <p className="text-xs text-[var(--app-text-muted)] py-2 text-center font-mono">Loading...</p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default MediaLibraryPickerModal;
